<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { saveEFilingData } from '@/features/startTask/api/taskApi';
import {
  ConfirmationControllersForm,
  createEmptyConfirmationControllersForm,
  applyEntityBasicsToConfirmationForm,
  buildConfirmationControllersTaskFormData,
  isConfirmationControllersFormComplete,
  buildSgPresetPrepareDocumentItem,
  SG_CONFIRMATION_CONTROLLERS_TASK_CODE,
  type ConfirmationControllersFormModel,
} from '@/features/startTask/preset_task/sg/components';
import { buildDirectorsSignersJson } from '@/features/startTask/preset_task/sg/components/allotment-shares/composables/allotmentSigners';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  /** Edit(수정) 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update task' + entity 자동 prefill skip(restoreState 가 채움) + create→update 분기.
const editMode = computed(() => !!props.editTaskId);

// restoreState 직후 입력 스냅샷(기준선). Update task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true; // Create 모드는 항상 활성
  if (editBaseline.value == null) return false; // 기준선 캡처 전 — 비활성
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
}>();

const router = useRouter();
const creating = ref(false);
const progress = ref('');

// 진행 상태를 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다. (로컬 creating/progress 는 버튼 :loading·인라인 에러용으로 유지)
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
const taskConfigSteps = ref<string[]>([]);
const taskTypeNameFromConfig = ref('');
const formModel = reactive<ConfirmationControllersFormModel>(createEmptyConfirmationControllersForm());
const formRef = ref<{ validateAndFocusRequiredFields?: () => Promise<boolean> } | null>(null);

const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const icpCd = computed(() => props.entityData?.icpCd || props.entityData?.entityCd || '');

function onRequestPersonLookup(ctx: Record<string, any>) {
  const ec = icpCd.value;
  emit('request-person-lookup', {
    ...ctx,
    entityCd: ec || ctx.entityCd,
    icpCd: ec || ctx.icpCd,
  });
}

watch(
  () => props.entityData,
  (ent) => {
    if (editMode.value) return; // Edit 모드: restoreState 가 채우므로 entity 자동 prefill skip.
    if (!ent) return;
    applyEntityBasicsToConfirmationForm(formModel, ent);
    const reg = String(ent.registrationNo || ent.regNo || ent.REG_NO || '').trim();
    if (reg) formModel.companyRegistrationNo = reg;
  },
  { immediate: true, deep: true },
);

onMounted(async () => {
  try {
    const cfg = await getTaskConfigByCode(SG_CONFIRMATION_CONTROLLERS_TASK_CODE);
    taskConfigSteps.value = cfg?.processSteps || [];
    taskTypeNameFromConfig.value = (cfg?.taskNameEn || cfg?.taskName || '').trim();
    const presetCode = String(props.selection.task.TASK_CODE || '').trim().toUpperCase();
    if (presetCode && presetCode !== SG_CONFIRMATION_CONTROLLERS_TASK_CODE) {
      console.warn(
        '[PreSetConfirmationControllers] L_PRESET_TASK TASK_CODE mismatch — using B32108 for task create',
        { presetCode, expected: SG_CONFIRMATION_CONTROLLERS_TASK_CODE },
      );
    }
  } catch (err) {
    console.warn('[PreSetConfirmationControllers] task config fetch failed:', err);
  }
});

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) {
    progress.value = 'Error: enter a Task Title in the chat first.';
    return;
  }
  if (!props.entityData) {
    progress.value = 'Error: select an entity in the chat first.';
    return;
  }
  const formValidation = await formRef.value?.validateAndFocusRequiredFields?.();
  if (formValidation === false || !isConfirmationControllersFormComplete(formModel)) {
    progress.value = 'Error: complete all required fields below.';
    return;
  }

  const taskTypeCd = SG_CONFIRMATION_CONTROLLERS_TASK_CODE;
  const taskTypeName =
    taskTypeNameFromConfig.value
    || 'Confirmation of Registrable Controllers';

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling'),
    };

    const signersJson = await buildDirectorsSignersJson(icpCd.value || '');
    const taskFormData: Record<string, any> = await buildConfirmationControllersTaskFormData(
      formModel,
      icpCd.value || '',
      signersJson,
    );
    // presetForm — Edit 모드 복원용 입력 스냅샷.
    taskFormData.presetForm = gatherState();
    const templateVars = taskFormData;
    const personName = formModel.companyName || title;

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: taskFormData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      // 제목 갱신 (선택)
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName,
          taskTitle: title,
          taskFlow,
          officerData: taskFormData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Saving task data...';
    try {
      await saveEFilingData(taskId, {
        taskTypeCd,
        entityCd: icpCd.value || null,
        countryCd: 'SG',
        formData: taskFormData as Record<string, unknown>,
        requestPayload: null,
        responseData: null,
        status: 'B36002',
        isSuccess: false,
      });
    } catch (e) {
      console.warn('[PreSetConfirmationControllers] L_TASK_EFILING_DATA save failed', e);
    }

    progress.value = 'Saving documents...';
    const docsToSave = (props.selection.documents || [])
      .filter((d) => d.tmpId)
      .map((d) =>
        buildSgPresetPrepareDocumentItem({
          templateId: d.tmpId as number | string,
          templateName: d.templateName,
          personName,
          commonFormData: { ...templateVars },
          entityCd: icpCd.value || undefined,
        }),
      );

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err: unknown) {
        console.warn('[PreSetConfirmationControllers] prepare-documents failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch {
      /* ignore */
    }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[PreSetConfirmationControllers] create task failed:', err);
    progress.value = `Error: ${msg}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
//   폼 상태는 단일 reactive formModel 하나 — 통째로 deep-clone.
function gatherState(): Record<string, any> {
  return {
    formModel: JSON.parse(JSON.stringify(formModel)),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.formModel && typeof s.formModel === 'object') {
    Object.assign(formModel, JSON.parse(JSON.stringify(s.formModel)));
  }
  // 파생 watcher 가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="ad-panel">
    <div class="ad-header">
      <div>
        <div class="ad-title">{{ selection.task.TASK_NAME }}</div>
        <div class="ad-subtitle">Confirmation of Registrable Controllers (Singapore)</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>
        Back
      </v-btn>
    </div>

    <div class="ad-content">
      <div class="ad-task-section">
        <div class="ad-task-section-header">
          <Icon icon="mdi:text-box-outline" width="16" color="#666" />
          <span>Task Title</span>
        </div>
        <div v-if="effectiveTaskTitle" class="d-flex align-start justify-space-between ga-3">
          <div class="ad-readonly-box flex-grow-1">{{ effectiveTaskTitle }}</div>
          <v-btn variant="tonal" color="primary" size="small" @click="emit('change-task-title')">
            <v-icon start size="16">mdi-pencil</v-icon>
            Change
          </v-btn>
        </div>
        <div v-else class="ad-readonly-box ad-readonly-empty">
          Waiting for task title (enter it in the chat on the left)...
        </div>
      </div>

      <div v-if="entityData" class="ad-entity">
        <div class="d-flex align-center ga-3 justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="ad-entity-icon">
              <Icon icon="mdi:domain" width="24" color="#1B84FF" />
            </div>
            <div>
              <div class="ad-entity-name">{{ formModel.companyName || entityData.companyName || '—' }}</div>
              <div v-if="entityData.registrationNo || formModel.companyRegistrationNo" class="ad-entity-reg">
                UEN / Reg. No.: {{ formModel.companyRegistrationNo || entityData.registrationNo }}
              </div>
            </div>
          </div>
          <v-btn variant="tonal" color="primary" size="small" @click="emit('change-entity')">
            <v-icon start size="16">mdi-swap-horizontal</v-icon>
            Change
          </v-btn>
        </div>
      </div>

      <div v-else class="ad-empty">
        <Icon icon="mdi:link-variant-plus" width="40" color="#d0d0d0" class="mb-2" />
        <div class="ad-empty-title">No entity linked</div>
        <div class="ad-empty-hint">Use the chat on the left to select a company.</div>
      </div>

      <div v-if="entityData" class="ad-form-wrap">
        <ConfirmationControllersForm
          ref="formRef"
          :model="formModel"
          :entity-cd="icpCd"
          :documents="selection.documents || []"
          :read-only="false"
          :selected-person="selectedPerson"
          :active-person-context="activePersonContext"
          @request-person-lookup="onRequestPersonLookup"
        />
      </div>

      <div v-if="selection.documents?.length" class="ad-doc-section">
        <div class="ad-doc-section-header">
          <Icon icon="mdi:file-document-multiple-outline" width="16" color="#5c6bc0" />
          <span>Documents ({{ selection.documents.length }})</span>
        </div>
        <div v-for="(doc, idx) in selection.documents" :key="doc.docCode || idx" class="ad-doc-row">
          <div class="ad-doc-num">{{ idx + 1 }}</div>
          <div>
            <div class="ad-doc-name">{{ doc.templateName }}</div>
            <div v-if="doc.docCode" class="ad-doc-code">{{ doc.docCode }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="creating || progress" class="ad-progress">
      <v-progress-linear v-if="creating" indeterminate color="primary" height="3" />
      <div class="ad-progress-text" :class="{ 'text-error': progress.startsWith('Error') }">
        {{ progress }}
      </div>
    </div>

    <div class="ad-footer">
      <v-btn variant="outlined" color="grey" size="small" @click="emit('back')">
        <Icon icon="mdi:arrow-left" width="16" class="mr-1" />
        Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :disabled="creating || !isReady || (editMode && !isDirty)"
              :loading="creating"
              @click="handleCreateTask"
            >
              <Icon :icon="editMode ? 'mdi:content-save' : 'mdi:check'" width="16" class="mr-1" />
              {{ editMode ? 'Update task' : 'Create task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<style scoped>
.ad-panel { height: 100%; display: flex; flex-direction: column; overflow: hidden; }
.ad-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 12px; border-bottom: 1px solid #f0f0f0; }
.ad-title { font-size: 18px; font-weight: 700; color: #1a1a2e; }
.ad-subtitle { font-size: 12px; color: #888; margin-top: 2px; }
.ad-content { flex: 1; overflow-y: auto; padding: 20px 24px; }
.ad-task-section { margin-bottom: 16px; background: #fff; border: 1px solid #e9edf5; border-radius: 12px; padding: 0 0 12px; overflow: hidden; }
.ad-task-section > :not(.ad-task-section-header) { padding-left: 14px; padding-right: 14px; }
.ad-task-section-header { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #283593; margin: 0 0 12px; padding: 10px 14px; background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%); border-bottom: 1px solid #d6dcf2; }
.ad-readonly-box { background: #fafafa; border: 1px solid #eee; border-radius: 6px; padding: 10px 12px; font-size: 13px; color: #333; line-height: 1.5; }
.ad-readonly-empty { color: #bbb; font-style: italic; }
.ad-entity { background: #f0f5ff; border: 1px solid #d6e4ff; border-radius: 12px; padding: 16px; margin-bottom: 16px; }
.ad-entity-icon { width: 40px; height: 40px; border-radius: 10px; background: #fff; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); }
.ad-entity-name { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.ad-entity-reg { font-size: 12px; color: #666; margin-top: 2px; }
.ad-empty { text-align: center; padding: 28px 16px; border: 1px dashed #ddd; border-radius: 12px; margin-bottom: 16px; }
.ad-empty-title { font-size: 14px; font-weight: 600; color: #666; }
.ad-empty-hint { font-size: 12px; color: #999; margin-top: 4px; }
.ad-doc-section { margin-bottom: 16px; background: #fff; border: 1px solid #e9edf5; border-radius: 12px; padding: 0 0 12px; overflow: hidden; }
.ad-doc-section-header { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #283593; margin: 0 0 12px; padding: 10px 14px; background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%); border-bottom: 1px solid #d6dcf2; }
.ad-doc-row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 8px; background: #fafbff; margin-bottom: 6px; margin-left: 14px; margin-right: 14px; }
.ad-doc-num { width: 22px; height: 22px; border-radius: 4px; background: #e3f2fd; font-size: 11px; font-weight: 700; color: #1565c0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ad-doc-name { font-size: 12px; font-weight: 500; }
.ad-doc-code { font-size: 10px; color: #999; }
.ad-progress { padding: 0 24px 12px; }
.ad-progress-text { font-size: 12px; color: #666; padding-top: 8px; }
.ad-footer { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 12px 24px 20px; border-top: 1px solid #f0f0f0; }
</style>
